import React, { useEffect, useState } from 'react';
import { 
  Modal, Form, Input, message, Rate, Button, 
  Popover, Pagination, Tag, Card, Divider, Descriptions, Avatar 
} from 'antd';
import { 
  FilePdfOutlined, ShoppingOutlined, CarOutlined, 
  CreditCardOutlined, EnvironmentOutlined, StarOutlined 
} from '@ant-design/icons';
import moment from 'moment';
import { get, post } from '../../services/ApiRouting';

import "../OrderCom/OrderList.css";
import "../../Css/MyOrder.css";
import { useCurrency } from '../../services/locationService';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://1fiveglobal.in';
const ITEMS_PER_PAGE = 5;

const AddressContent = ({ address_line1, city, state, postal_code, country_name }) => (
  <div className='Order-Details-Address p-1'>
    <p className="m-0 fw-semibold">{address_line1}</p>
    <p className="m-0 text-secondary">{city}, {state} {postal_code}</p>
    <p className="m-0 text-secondary">{country_name}</p>
  </div>
);

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'delivered': return 'success';
    case 'processing': return 'processing';
    case 'cancelled': return 'error';
    case 'shipped': return 'warning';
    default: return 'default';
  }
};

const OrderList = () => {
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [reviewModal, setReviewModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [productID, setProductID] = useState(null);
  const [yourOrderProduct, setYourOrderProduct] = useState(null);
  const { format } = useCurrency();

  const [form] = Form.useForm();
  const profile = JSON.parse(localStorage.getItem('profile') || '{}');

  const handleOpenDetailsModal = (order) => {
    setOrderDetails(order);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
  };

  const handleReviewModal = (id) => {
    setProductID(id);
    setReviewModal(true);
  };

  const fetchData = async (page) => {
    const userId = profile?.user_id || profile?.id || 7;
    try {
      const response = await get(
        `/payments/orders?user_id=${userId}&page=${page}&limit=${ITEMS_PER_PAGE}`
      );

      if (response?.success) {
        setYourOrderProduct(response.orders || []);
        setTotalItems(response?.pagination?.total || 0);
      } else {
        setYourOrderProduct([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to load orders.');
      setYourOrderProduct([]);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handleDownloadInvoice = (pdfPath) => {
    if (!pdfPath) {
      message.warning('Invoice not available yet.');
      return;
    }
    const pdfUrl = `${BASE_URL}/${pdfPath.startsWith('/') ? pdfPath.slice(1) : pdfPath}`;
    window.open(pdfUrl, '_blank');
  };

  const onFinish = async (values) => {
    if (!profile?.user_id && !profile?.id) {
      message.error('Please Login First.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        review: values.reviewContent,
        stars: values.rating,
        user_id: profile?.user_id || profile?.id,
        product_id: productID,
      };

      const response = await post('/review.php', payload);
      if (response) {
        message.success('Review submitted successfully');
        form.resetFields();
        setReviewModal(false);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      message.error('Submission failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className='order-section-order-list-main-con'>
      <div className='container'>
        <div className='order-section-order-list-head mb-4'>
          <h2>My Orders</h2>
        </div>
        <div className='order-section-order-list-con'>
          <div className='MyOrder-Section-con'>
            {!yourOrderProduct ? (
              <p>Loading orders...</p>
            ) : yourOrderProduct.length === 0 ? (
              <div className='order-section-empty-list-text text-center py-5'>
                <h2>No orders found</h2>
                <h3 className="text-muted">Go to store to place an order.</h3>
              </div>
            ) : (
              yourOrderProduct.map((order) => {
                const {
                  order_id,
                  order_number,
                  total_amount,
                  order_status,
                  order_created_at,
                  user_name,
                  address_line1,
                  city,
                  state,
                  postal_code,
                  country_name,
                  invoice_pdf,
                  items = []
                } = order;

                return (
                  <div className='Product-addedCart-Side-Bar orderlistmaincon mb-4' key={order_id}>
                    <div className='Orderlist-Details-Head-Con p-3 bg-light rounded-top'>
                      <div className='row align-items-center'>
                        <div className='col-md-3 col-sm-4 col-12'>
                          <div className='OrderList-Details-Date'>
                            <span className="text-uppercase text-muted small fw-bold">Order Placed</span>
                            <h3 className="fs-6 m-0">{moment(order_created_at).format("ll")}</h3>
                          </div>
                        </div>
                        <div className='col-md-3 col-sm-4 col-6'>
                          <div className='OrderList-Details-Date'>
                            <span className="text-uppercase text-muted small fw-bold">Ship To</span>
                            <h3 className="fs-6 m-0">
                              <Popover
                                placement="bottom"
                                content={
                                  <AddressContent
                                    address_line1={address_line1}
                                    city={city}
                                    state={state}
                                    postal_code={postal_code}
                                    country_name={country_name}
                                  />
                                }
                              >
                                <Button type="link" className='p-0 text-dark fw-semibold'>
                                  {user_name}
                                </Button>
                              </Popover>
                            </h3>
                          </div>
                        </div>
                        <div className='col-md-2 col-sm-4 col-6'>
                          <div className='OrderList-Details-Date'>
                            <span className="text-uppercase text-muted small fw-bold">Total</span>
                            <h3 className="fs-6 m-0 fw-bold">{format(total_amount)}</h3>
                          </div>
                        </div>
                        <div className='col-md-4 col-12 text-md-end mt-2 mt-md-0'>
                          <div className='OrderList-Details-Date orderidcon'>
                            <span className="text-uppercase text-muted small fw-bold">Order #{order_number}</span>
                            <div>
                              <Tag color={getStatusColor(order_status)} className="mt-1 me-0 text-uppercase fw-bold">
                                {order_status}
                              </Tag>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Items List */}
                    <div className="p-3">
                      {items.map((item) => {
                        const {
                          order_item_id,
                          product_id,
                          product_name,
                          productName,
                          product_title,
                          name,
                          product,
                          product_code,
                          price,
                          quantity,
                          color_name,
                          size_name,
                          order_item_image_url
                        } = item;
                        const displayProductName = product_name || productName || product_title || name || product?.name || 'Product';

                        const fullImageUrl = order_item_image_url?.startsWith('http')
                          ? order_item_image_url
                          : `${BASE_URL}${order_item_image_url}`;

                        return (
                          <div className='Product-addedCart-Side-details orderList-con py-2 border-bottom' key={order_item_id}>
                            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
                              <div className='d-flex align-items-center gap-3'>
                                <Avatar shape="square" size={64} src={fullImageUrl} />
                                <div>
                                  <h4 
  className="m-0 fw-semibold fs-6" 
  style={{ color: 'var(---color01) !important' }}
>
  {displayProductName} <span className="text-muted fs-7">({product_code})</span>
</h4>
                                  <p className="m-0 text-muted small">Variant: {color_name} | {size_name}</p>
                                  <p className="m-0 text-dark small fw-medium">Qty: {quantity} × {format(price)}</p>
                                </div>
                              </div>

                              {order_status === 'Delivered' && (
                                <Button 
                                  type="default" 
                                  icon={<StarOutlined />}
                                  onClick={() => handleReviewModal(product_id)}
                                >
                                  Write Review
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Actions Footer */}
                    <div className='p-3 bg-light-subtle d-flex gap-2 justify-content-end rounded-bottom'>
                      {invoice_pdf && (
                        <Button icon={<FilePdfOutlined />} onClick={() => handleDownloadInvoice(invoice_pdf)}>
                          Invoice
                        </Button>
                      )}
                      <Button type="primary" onClick={() => handleOpenDetailsModal(order)}>
                        View Order Details
                      </Button>
                    </div>
                  </div>
                );
              })
            )}

            {totalItems > ITEMS_PER_PAGE && (
              <div className="d-flex justify-content-center mt-4">
                <Pagination
                  current={currentPage}
                  total={totalItems}
                  pageSize={ITEMS_PER_PAGE}
                  onChange={(page) => setCurrentPage(page)}
                />
              </div>
            )}
          </div>

          {/* Details Modal (Replaced Drawer) */}
          <Modal
            title={
              <div className="d-flex align-items-center justify-content-between pe-4">
                <span className="fw-bold fs-5">Order Details - #{orderDetails?.order_number || ''}</span>
                {orderDetails?.order_status && (
                  <Tag color={getStatusColor(orderDetails.order_status)} className="text-uppercase fw-bold m-0">
                    {orderDetails.order_status}
                  </Tag>
                )}
              </div>
            }
            open={detailsModalOpen}
            onCancel={handleCloseDetailsModal}
            width={750}
            footer={[
              orderDetails?.invoice_pdf && (
                <Button 
                  key="invoice" 
                  type="primary" 
                  icon={<FilePdfOutlined />} 
                  onClick={() => handleDownloadInvoice(orderDetails.invoice_pdf)}
                >
                  Download Invoice
                </Button>
              ),
              <Button key="close" onClick={handleCloseDetailsModal}>
                Close
              </Button>
            ]}
            centered
          >
            {orderDetails && (
              <div className='d-flex flex-column gap-3 py-2'>
                {/* Meta Overview */}
                <Card size="small" className="bg-light border-0">
                  <Descriptions column={{ xs: 1, sm: 2 }} size="small" title="Overview">
                    <Descriptions.Item label="Order Date">{moment(orderDetails.order_created_at).format("LLL")}</Descriptions.Item>
                    <Descriptions.Item label="Payment Method">{orderDetails.payment_method || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Payment Status">
                      <Tag color={orderDetails.payment_status === 'Completed' ? 'success' : 'warning'}>
                        {orderDetails.payment_status}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Fulfillment Status">{orderDetails.order_status}</Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Items Section */}
                <div>
                  <h4 className="fw-bold fs-6 mb-2"><ShoppingOutlined /> Items Ordered</h4>
                  <Card size="small" className="p-0">
                    {orderDetails?.items?.map((prod, idx) => (
                      <div key={prod.order_item_id}>
                        <div className='d-flex align-items-center justify-content-between p-2'>
                          <div className='d-flex align-items-center gap-3'>
                            <Avatar shape="square" size={54} src={`${BASE_URL}${prod.order_item_image_url}`} />
                            <div>
                              <h5 className="m-0 fw-semibold fs-6">{prod.product_name}</h5>
                              <p className='m-0 text-muted small'>Code: {prod.product_code}</p>
                              <p className='m-0 text-muted small'>Variant: {prod.color_name} | {prod.size_name}</p>
                            </div>
                          </div>
                          <div className="text-end">
                            <p className='m-0 fw-semibold'>{format(prod.price * prod.quantity)}</p>
                            <span className='text-muted small'>Qty: {prod.quantity} × {format(prod.price)}</span>
                          </div>
                        </div>
                        {idx < orderDetails.items.length - 1 && <Divider className="m-0" />}
                      </div>
                    ))}
                  </Card>
                </div>

                {/* Logistics & Address Grid */}
                <div className="row g-3">
                  <div className="col-md-6">
                    <Card size="small" title={<><EnvironmentOutlined /> Delivery Address</>} className="h-100">
                      <p className='m-0 fw-bold'>{orderDetails.user_name}</p>
                      <p className='m-0 text-muted small'>{orderDetails.address_line1}</p>
                      <p className='m-0 text-muted small'>{orderDetails.city}, {orderDetails.state} - {orderDetails.postal_code}</p>
                      <p className='m-0 text-muted small mt-1'>Phone: {orderDetails.delivery_phone || orderDetails.user_phone}</p>
                    </Card>
                  </div>
                  <div className="col-md-6">
                    <Card size="small" title={<><CarOutlined /> Shipping Method</>} className="h-100">
                      <p className='m-0 fw-medium'>Standard Shipping</p>
                      <p className='m-0 text-muted small'>Status: {orderDetails.order_status}</p>
                    </Card>
                  </div>
                </div>

                {/* Payment Breakdown */}
                <div>
                  <h4 className="fw-bold fs-6 mb-2"><CreditCardOutlined /> Payment Summary</h4>
                  <Card size="small">
                    <div className='d-flex justify-content-between mb-1'>
                      <span className='text-muted'>Subtotal</span>
                      <span>{format(orderDetails.subtotal)}</span>
                    </div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span className='text-muted'>Shipping Fee</span>
                      <span>{format(orderDetails.shipping_amount)}</span>
                    </div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span className='text-muted'>Discount</span>
                      <span className="text-success">-{format(orderDetails.discount_amount)}</span>
                    </div>
                    <Divider className="my-2" />
                    <div className='d-flex justify-content-between align-items-center fw-bold fs-6'>
                      <span>Total Paid</span>
                      <span className="text-primary fs-5">{format(orderDetails.total_amount)}</span>
                    </div>
                  </Card>
                </div>
              </div>
            )}
          </Modal>

          {/* Review Modal */}
          <Modal
            title="Write a Review"
            open={reviewModal}
            onCancel={() => setReviewModal(false)}
            footer={null}
            centered
          >
            <Form form={form} onFinish={onFinish} layout="vertical">
              <Form.Item name="rating" label="Rating" rules={[{ required: true, message: 'Please set a rating' }]}>
                <Rate />
              </Form.Item>
              <Form.Item name="reviewContent" label="Review" rules={[{ required: true, message: 'Please write your review' }]}>
                <Input.TextArea rows={4} placeholder="Write your experience with this product..." />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                Submit Review
              </Button>
            </Form>
          </Modal>
        </div>
      </div>
    </section>
  );
};

export default OrderList;